import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import {
  acknowledgeHandoff,
  createHandoff,
  setHandoffPo,
  createSubmission,
  decideSubmission,
  fetchHandoffLinks,
  fetchHandoffs,
  fetchSubmissionPhotos,
  fetchSubmissions,
  updateSubmissionNotes,
} from './queries';
import type { HandoffInput, SubmissionInput } from './queries';
import type { SubmissionStatus } from './types';

export const exchangeKeys = {
  all: ['supplierExchange'] as const,
  submissions: () => [...exchangeKeys.all, 'submissions'] as const,
  photos: (submissionId: string) => [...exchangeKeys.all, 'photos', submissionId] as const,
  handoffs: () => [...exchangeKeys.all, 'handoffs'] as const,
  links: (handoffId: string) => [...exchangeKeys.all, 'links', handoffId] as const,
};

function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useSubmissions() {
  const { client } = useAuth();
  return useQuery({
    queryKey: exchangeKeys.submissions(),
    queryFn: () => fetchSubmissions(client),
    retry: false,
  });
}

export function useSubmissionPhotos(submissionId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: exchangeKeys.photos(submissionId),
    queryFn: () => fetchSubmissionPhotos(client, submissionId),
    enabled: submissionId !== '',
  });
}

export function useCreateSubmission() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (input: Omit<SubmissionInput, 'createdBy'>) =>
      createSubmission(client, { ...input, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: exchangeKeys.submissions() });
    },
  });
}

export function useUpdateSubmissionNotes() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      submissionId,
      changes,
    }: {
      submissionId: string;
      changes: { title?: string; notes?: string };
    }) => updateSubmissionNotes(client, submissionId, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: exchangeKeys.submissions() });
    },
  });
}

/**
 * The legacy flat photo upload was removed when component slots landed
 * (Batch 5 Step 3). Photos now arrive as submission_options, one per
 * component, through useCreateOption — a second door into submission_photos
 * would let an upload skip the slot rules entirely.
 */

export function useDecideSubmission() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      submissionId,
      status,
      note,
    }: {
      submissionId: string;
      status: SubmissionStatus;
      note: string;
    }) => decideSubmission(client, submissionId, status, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: exchangeKeys.submissions() });
    },
  });
}

export function useHandoffs() {
  const { client } = useAuth();
  return useQuery({
    queryKey: exchangeKeys.handoffs(),
    queryFn: () => fetchHandoffs(client),
    retry: false,
  });
}

export function useHandoffLinks(handoffId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: exchangeKeys.links(handoffId),
    queryFn: () => fetchHandoffLinks(client, handoffId),
    enabled: handoffId !== '',
  });
}

export function useCreateHandoff() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (input: Omit<HandoffInput, 'createdBy'>) =>
      createHandoff(client, { ...input, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: exchangeKeys.handoffs() });
    },
  });
}

export function useSetHandoffPo() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ handoffId, orderId }: { handoffId: string; orderId: string }) =>
      setHandoffPo(client, handoffId, orderId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: exchangeKeys.handoffs() });
    },
  });
}

export function useAcknowledgeHandoff() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const userId = useActorId();
  return useMutation({
    mutationFn: ({ handoffId, note }: { handoffId: string; note: string }) =>
      acknowledgeHandoff(client, handoffId, userId, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: exchangeKeys.handoffs() });
    },
  });
}
